<script lang="ts">
	import { format } from 'svelte-i18n';
	import { createEventDispatcher } from 'svelte';
	import { Input } from '@Shelly/shelly-design-system';

	export let value: string = '00:00';
	export let id: string;

	const dispatch = createEventDispatcher<{ change: string }>();

	/** Clamps `val` to the inclusive range [min, max]. */
	const clamp = (val: number, min: number, max: number): number => Math.max(min, Math.min(max, val));

	/** Zero-pads a number to at least 2 digits (e.g. 5 → "05"). */
	const pad = (n: number): string => String(n).padStart(2, '0');

	/** Parses a string to an integer and clamps it to valid hours (0–23). */
	const parseHours = (v: string) => clamp(parseInt(v, 10) || 0, 0, 23);

	/** Parses a string to an integer and clamps it to valid minutes (0–59). */
	const parseMins = (v: string) => clamp(parseInt(v, 10) || 0, 0, 59);

	let hoursStr = pad(parseHours(value?.split(':')[0] ?? '0'));
	let minsStr = pad(parseMins(value?.split(':')[1] ?? '0'));

	let prevValue = value;
	$: if (value !== prevValue) {
		prevValue = value;
		hoursStr = pad(parseHours(value?.split(':')[0] ?? '0'));
		minsStr = pad(parseMins(value?.split(':')[1] ?? '0'));
	}

	/**
	 * Clamps and formats the hours field on blur, then dispatches the updated time.
	 */
	const onHoursBlur = () => {
		const h = parseHours(hoursStr);
		hoursStr = pad(h);
		dispatch('change', `${hoursStr}:${minsStr}`);
	};

	/**
	 * Clamps and formats the minutes field on blur, then dispatches the updated time.
	 */
	const onMinsBlur = () => {
		const m = parseMins(minsStr);
		minsStr = pad(m);
		dispatch('change', `${hoursStr}:${minsStr}`);
	};

	/**
	 * Increments or decrements hours by 1 on ArrowUp / ArrowDown,
	 * clamped to 0–23, and dispatches immediately.
	 * @param e - The keyboard event from the hours input.
	 */
	const onHoursKeydown = (e: KeyboardEvent) => {
		if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
		e.preventDefault();
		const h = clamp(parseHours(hoursStr) + (e.key === 'ArrowUp' ? 1 : -1), 0, 23);
		hoursStr = pad(h);
		dispatch('change', `${hoursStr}:${minsStr}`);
	};

	/**
	 * Increments or decrements minutes by 1 on ArrowUp / ArrowDown,
	 * clamped to 0–59, and dispatches immediately.
	 * @param e - The keyboard event from the minutes input.
	 */
	const onMinsKeydown = (e: KeyboardEvent) => {
		if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
		e.preventDefault();
		const m = clamp(parseMins(minsStr) + (e.key === 'ArrowUp' ? 1 : -1), 0, 59);
		minsStr = pad(m);
		dispatch('change', `${hoursStr}:${minsStr}`);
	};
</script>

<div class="time-input">
	<Input
		id={`${id}-h`}
		aria-label={$format('daisy_em_tariff_time_input_hours', {
			default: 'Hours',
		})}
		size="m"
		type="text"
		inputmode="numeric"
		icon={null}
		value={hoursStr}
		on:input={(e) => (hoursStr = e.target?.['value'] ?? hoursStr)}
		on:blur={onHoursBlur}
		on:keydown={(e) => onHoursKeydown(e.detail ?? e)}
	/>
	<span class="separator">:</span>
	<Input
		id={`${id}-m`}
		aria-label={$format('daisy_em_tariff_time_input_minutes', {
			default: 'Minutes',
		})}
		size="m"
		type="text"
		inputmode="numeric"
		icon={null}
		value={minsStr}
		on:input={(e) => (minsStr = e.target?.['value'] ?? minsStr)}
		on:blur={onMinsBlur}
		on:keydown={(e) => onMinsKeydown(e.detail ?? e)}
	/>
</div>

<style>
	.time-input {
		display: flex;
		align-items: center;
		gap: var(--space-xs, 4px);
		width: 100%;
	}

	.separator {
		font-size: 16px;
		font-weight: bold;
		color: var(--color-white);
		flex-shrink: 0;
	}
</style>
